<template>
    <SearchBar
        :loading="loading"
        :showCloseButton="showCloseButton"
        @submit="handleSubmit"
        @close="closeForm"
    >
        <template #inputs>
            <div
                class="sm:w-[85%] md:h-10 bg-white dark:bg-slate-800 flex items-center flex-col md:flex-row gap-1"
            >
                <!-- START DESTINATION INPUT -->
                <div
                    class="w-full md:w-1/3 lg:w-1/2 h-10 pl-4 pr-3 py-2.5 bg-white dark:bg-slate-800 justify-start items-center gap-2 flex rounded relative"
                    :class="{
                        'ring-red-600 ring-1':
                            !query && errors.includes('destination'),
                    }"
                >
                    <div
                        class="w-5 h-5 flex align-center text-[#808080]"
                        :class="{
                            'text-red-500':
                                !query && errors.includes('destination'),
                        }"
                    >
                        <i class="fa-regular fa-location-dot"></i>
                    </div>
                    <span class="w-full flex items-center">
                        <span
                            v-show="showNearLabel"
                            class="text-black text-sm font-medium border-none"
                        >
                            Near:
                        </span>
                        <input
                            type="text"
                            name="destination"
                            id="destination"
                            v-model="query"
                            @input="handleInput"
                            @focus="handleFocus"
                            ref="destination_input"
                            placeholder="City, Airport Code, or Zip"
                            class="text-black text-sm font-medium border-none w-full focus:ring-0 relative"
                        />
                    </span>
                    <RecentSearches
                        v-show="!hideSuggestions"
                        :searches="
                            suggestedSearches.length
                                ? suggestedSearches
                                : recentSearches
                        "
                        v-click-outside="() => (hideSuggestions = true)"
                        @searchSelected="handleSearchSelected"
                        @tabAway="() => (hideSuggestions = true)"
                    >
                        <template #default="{ search }">
                            <div class="flex flex-col">
                                <div class="text-sm font-medium text-gray-900">
                                    {{ search.keywords }}
                                </div>
                                <div class="text-xs text-gray-500">
                                    {{
                                        formatDate(search.start_date) +
                                        ' - ' +
                                        formatDate(search.end_date)
                                    }}
                                </div>
                            </div>
                        </template>
                    </RecentSearches>
                    <div
                        class="h-fit w-full bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/50 justify-center items-center flex flex-col absolute inset-y-full left-0 mt-3 z-10"
                        v-if="
                            showOnMap &&
                            !filteredSearchResults.length &&
                            searchCompleted &&
                            hideSuggestions
                        "
                    >
                        <EmptyState
                            title="No matching hotels found"
                            description="Try changing your search parameters."
                            :hasAction="false"
                        />
                    </div>

                    <SearchFormValidationErrors
                        :errors="errors"
                        errorType="destination"
                        errorMessage="Please enter a destination"
                    />
                    <ClearInputButton
                        v-show="query"
                        :onClear="() => handleInput({ target: { value: '' } })"
                        class="pr-2 md:p-0"
                    />
                </div>
                <div class="w-full md:w-px h-px md:h-8 bg-zinc-300"></div>
                <!-- START CHECK IN DATE -->
                <div
                    class="w-full md:w-1/4 grow h-10 px-4 py-2.5 bg-white dark:bg-slate-800 border border-black/0 justify-start items-center gap-2 flex rounded"
                    :class="{
                        'ring-red-600 ring-1':
                            !checkIn && errors.includes('checkIn'),
                    }"
                >
                    <div
                        class="w-5 h-6 relative text-[#808080]"
                        :class="{
                            'text-red-500':
                                !checkIn && errors.includes('checkIn'),
                        }"
                    >
                        <i class="fa-thin fa-calendar-days"></i>
                    </div>
                    <label for="start_date" class="sr-only">Check-in</label>
                    <flat-pickr
                        :config="dateConfig"
                        v-model="checkIn"
                        name="start_date"
                        id="check-in-date"
                        placeholder="Check-In"
                        ref="checkInRef"
                        class="border-red-400 w-full text-black text-sm font-medium leading-tight border-none cursor-pointer focus:ring-0 realtive"
                    />
                    <SearchFormValidationErrors
                        :errors="errors"
                        errorType="checkIn"
                        errorMessage="Please enter a start date"
                    />
                    <SearchFormValidationErrors
                        :errors="errors"
                        errorType="dateRange"
                        errorMessage="Start date must be before end date"
                    />
                    <ClearInputButton
                        v-show="checkIn"
                        :onClear="() => handleClearDate('checkIn', '')"
                    />
                </div>
                <!-- END CHECK IN DATE -->
                <div class="w-full md:w-px h-px md:h-8 bg-zinc-300"></div>
                <div
                    class="w-full md:w-1/4 grow h-10 px-4 py-2.5 bg-white dark:bg-slate-800 border border-black/0 justify-start items-center gap-2 flex rounded"
                    :class="{
                        'ring-red-600 ring-1': errors.includes('checkOut'),
                    }"
                >
                    <!-- START CHECK OUT DATE -->
                    <div
                        class="w-5 h-6 relative text-[#808080]"
                        :class="{
                            'text-red-500':
                                !checkOut && errors.includes('checkOut'),
                        }"
                    >
                        <i class="fa-thin fa-calendar-days"></i>
                    </div>
                    <flat-pickr
                        :config="dateConfig"
                        name="end-date"
                        id="check-out-date"
                        v-model="checkOut"
                        placeholder="Check-Out"
                        ref="checkOutRef"
                        class="w-full text-black text-sm font-medium leading-tight border-none cursor-pointer focus:ring-none focus:ring-0 relative"
                    />
                    <SearchFormValidationErrors
                        :errors="errors"
                        errorType="checkOut"
                        errorMessage="Please enter an end date"
                    />
                    <ClearInputButton
                        v-show="checkOut"
                        :onClear="() => handleClearDate('checkOut', '')"
                    />
                </div>
            </div>
        </template>
        <template #buttons>
            <div
                class="w-full lg:w-[15%] flex items-center pt-3 md:pt-0"
                :class="{
                    'justify-between': showCloseButton,
                    'justify-end': !showCloseButton,
                }"
            >
                <button
                    v-if="showCloseButton"
                    type="button"
                    @click="closeForm"
                    :disabled="loading"
                    class="md:hidden px-4 py-2.5 bg-slate-200 rounded justify-center items-center gap-2 flex text-slate-800 hover:bg-slate-300 active:bg-slate-400 focus:outline-slate-200 focus:shadow disabled:opacity-50 disabled:bg-slate-600"
                >
                    <div class="text-sm font-medium leading-tight">Close</div>
                </button>
                <button
                    type="button"
                    @click="handleSubmit"
                    @keyup.enter="handleSubmit"
                    :disabled="loading"
                    class="px-4 py-2.5 bg-orange-600 rounded justify-center items-center gap-2 flex text-white hover:bg-red-400 active:bg-red-400 focus:outline-slate-200 focus:shadow disabled:opacity-50 disabled:bg-orange-600"
                    :class="{ 'w-full': !showCloseButton }"
                >
                    <div class="w-5 h-5">
                        <i class="fa-solid fa-magnifying-glass fa-xs"></i>
                    </div>
                    <div class="text-sm font-medium leading-tight">Search</div>
                </button>
            </div>
        </template>
    </SearchBar>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCalendarStore } from '@/store/calendar';
import { useHotelsStore } from '@/store/hotels';
import { usePointsStore } from '@/store/points';
import { useTransferPartnersStore } from '@/store/transferpartners';
import dayjs from 'dayjs';
import vClickOutside from 'click-outside-vue3';
import SearchBar from '@/components/ui/inputs/search-bar/search-bar.vue';
import SearchFormValidationErrors from '@/components/hotels/search/search-form-validation-errors/search-form-validation-errors.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import RecentSearches from '@/components/tools/recent-searches/recent-searches.vue';
import ClearInputButton from '@/components/ui/clear-input-button/clear-input-button.vue';
import { useRewardsAndTransferPartners } from '@/composables/useRewardsAndTransferPartners.vue';

export default {
    components: {
        ClearInputButton,
        EmptyState,
        FlatPickr,
        RecentSearches,
        SearchBar,
        SearchFormValidationErrors,
    },
    props: {
        showCloseButton: {
            type: Boolean,
            default: false,
        },
        origin: {
            type: String,
            default: 'searchResults',
        },
        showNearLabel: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const { initializePointsAndTransfersData } =
            useRewardsAndTransferPartners();
        return { initializePointsAndTransfersData };
    },
    data: () => ({
        errors: [],
        dateConfig: {
            dateFormat: 'm/d/Y',
            minDate: 'today',
            maxDate: dayjs().add(13, 'month').format('MM/DD/YYYY'),
        },
        hideSuggestions: true,
        suggestedSearches: [],
        userInput: '',
        startDateRef: null,
        endDateRef: null,
    }),
    directives: {
        ClickOutside: vClickOutside.directive,
    },
    computed: {
        ...mapState(useHotelsStore, [
            'searching',
            'loading',
            'searchQuery',
            'usersRecentSearches',
            'searchCounter',
            'showOnMap',
            'filteredSearchResults',
            'searchCompleted',
        ]),
        ...mapState(useCalendarStore, ['startDate', 'endDate']),
        query: {
            get() {
                return this.searchQuery;
            },
            set(val) {
                this.setSearchQuery(val);
            },
        },
        checkIn: {
            get() {
                return this.startDate;
            },
            set(val) {
                this.setStartDate(val);
            },
        },
        checkOut: {
            get() {
                return this.endDate;
            },
            set(val) {
                this.setEndDate(val);
            },
        },
        isValidDateRange() {
            let fromDate = this.startDate,
                toDate = this.endDate,
                fdate = new Date(fromDate),
                tdate = new Date(toDate);
            if (fdate.valueOf() > tdate.valueOf() || !toDate) {
                return false;
            }
            return true;
        },
        recentSearches() {
            return this.usersRecentSearches.slice(0, 5);
        },
    },
    watch: {
        checkIn(val) {
            if (val) {
                this.removeError('checkIn');
            }
            if (val && !this.isValidDateRange) {
                let d = new Date(this.startDate);
                this.checkOut = d.setDate(d.getDate() + 1);
            }
        },
        checkOut(val) {
            if (val) {
                this.removeError('checkOut');
            }
        },
    },
    methods: {
        ...mapActions(useHotelsStore, [
            'setSearchQuery',
            'nextPage',
            'prevPage',
            'search',
            'setCurrentPage',
            'sortProperties',
            'getRecentSearches',
            'setSearchCompleted',
            'setSearchLocation',
        ]),
        ...mapActions(useCalendarStore, [
            'setDateRange',
            'setStartDate',
            'setEndDate',
        ]),
        ...mapActions(usePointsStore, ['getRewards']),
        ...mapActions(useTransferPartnersStore, [
            'getAllGroupTransferPartners',
        ]),

        filterOptions(input) {
            this.suggestedSearches = this.usersRecentSearches.filter(
                (searchObj) => searchObj.keywords.includes(input)
            );
            this.hideSuggestions = !this.suggestedSearches.length;
        },
        handleClearDate(dateType, val) {
            this[dateType] = val;
        },
        async handleFocus() {
            await this.getRecentSearches();
            setTimeout(() => (this.hideSuggestions = false), 200);
        },
        handleInput(e) {
            this.setSearchCompleted(false);
            this.removeError('destination');
            this.userInput = e.target.value;
            this.query = this.userInput;
            this.setSearchLocation(null);
            //do we need both this.userInput and this.query? Single source of truth?
            if (this.userInput.trim().length) {
                this.filterOptions(this.userInput);
            } else {
                this.suggestedSearches = [];
            }
        },
        handleSearchSelected(search) {
            this.setSearchQuery(search.keywords);
            this.setStartDate(new Date(search.start_date));
            this.setEndDate(new Date(search.end_date));
            this.resetErrors();
            this.hideSuggestions = true;
            this.searchProperties();
        },
        async handleSubmit() {
            this.hideSuggestions = true;
            this.validate();
            if (Object.keys(this.errors).length === 0) {
                await this.searchProperties();
                this.getRecentSearches();
            }
        },
        closeForm() {
            this.$emit('close-form');
        },
        resetErrors() {
            this.errors = [];
        },
        removeError(type) {
            const i = this.errors.indexOf(type);
            if (i !== null) {
                this.errors.splice(i, 1);
            }
        },
        async searchProperties() {
            this.sortProperties(null, 'search');
            this.setCurrentPage(1);
            this.search();

            const start = dayjs(this.startDate).format('MM/DD/YY');
            const end = dayjs(this.endDate).format('MM/DD/YY');

            this.$router.push(
                `/hotels/search?destination=${this.query}&start_date=${start}&end_date=${end}`,
                true
            );
        },
        validate() {
            this.errors = [];
            if (!this.endDate) {
                this.errors.push('checkOut');
                this.$refs.checkOutRef.getElem().focus();
            }

            if (!this.startDate) {
                this.errors.push('checkIn');
                this.$refs.checkInRef.getElem().focus();
            }

            if (!this.query) {
                this.errors.push('destination');
                this.$refs.destination_input.focus();
            }

            if (this.startDate && this.endDate) this.validateDateRange();
        },
        validateDateRange() {
            if (!this.isValidDateRange) {
                this.errors.push('dateRange');
                this.checkOut = null;
                this.$refs.checkInRef.getElem().focus();
            }
        },
    },
    mounted() {
        this.initializePointsAndTransfersData();
        this.query =
            (this.$route.query.destination || this.searchQuery) ?? null;
        this.checkIn = this.$route.query.start_date
            ? new Date(this.$route.query.start_date)
            : null;
        this.checkOut = this.$route.query.end_date
            ? new Date(this.$route.query.end_date)
            : null;
        if (this.checkIn && this.checkOut && this.origin !== 'map') {
            this.searchProperties();
        }
    },
};
</script>
